/**
 * @fileoverview Unified sidebar driven by CaseIngestionResult state.
 *
 * This component owns the polling lifecycle:
 * - Polls CaseIngestionResult while ingestion is not in a terminal state
 * - Transitions naturally through phases based on the result
 *
 * Phases:
 * 1. pending / in_progress (no handle) → Spinner
 * 2. in_progress + browser_use handle → BrowserAgentViewer (compact, live)
 * 3. completed → CMS Assist panel + mini-player at bottom
 * 4. failed → Error banner + CMS Assist panel + mini-player at bottom
 * 5. null (no ingestion) → CMS Assist panel only
 */
import { AlertCircle, Loader2, X } from 'lucide-react';
import React, { useCallback, useState } from 'react';

import { BrowserAgentViewer } from '@/components/browser-use-scraper';
import { Button } from '@/components/ui/button';
import { useGetCaseIngestionQuery } from '@/services/api/caseIngestionService';

import { BrowserAgentMiniPlayer } from '../ingestion-workflow/BrowserAgentMiniPlayer';
import { CaseIngestionAssistPanel } from './CaseIngestionAssistPanel';
import type { CaseIngestionAssistState } from './types';

interface UnifiedAssistSidebarProps {
  matterId: string;
  assistState: CaseIngestionAssistState;
  onClose: () => void;
  currentStepId?: string;
}

const ACTIVE_POLL_INTERVAL_MS = 2000;

function isTerminalStatus(status: string | null | undefined): boolean {
  return status === 'completed' || status === 'failed';
}

function isActiveStatus(status: string | null | undefined): boolean {
  return status === 'pending' || status === 'in_progress';
}

const IngestionSpinner: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
    <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
    <p className="text-sm text-muted-foreground">{label}</p>
  </div>
);

const IngestionErrorBanner: React.FC<{ error: string | null }> = ({ error }) => (
  <div className="border-b border-red-200 bg-red-50 px-4 py-3 dark:border-red-800 dark:bg-red-950">
    <div className="flex items-start gap-2">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
      <div>
        <p className="text-xs font-medium text-red-700 dark:text-red-400">Ingestion failed</p>
        {error && (
          <p className="mt-1 text-[11px] text-red-600 dark:text-red-300 break-words">{error}</p>
        )}
      </div>
    </div>
  </div>
);

export const UnifiedAssistSidebar: React.FC<UnifiedAssistSidebarProps> = ({
  matterId,
  assistState,
  onClose,
  currentStepId,
}) => {
  // Always poll while the sidebar is mounted. When it's closed it unmounts
  // and polling stops automatically. This is the simplest way to guarantee
  // we catch the in_progress → completed transition without stale state.
  const { data: result } = useGetCaseIngestionQuery(matterId, {
    pollingInterval: ACTIVE_POLL_INTERVAL_MS,
  });

  // Snap-to-URL state: set by source URL chips, consumed by BrowserAgentViewer
  const [snapToUrl, setSnapToUrl] = useState<string | null>(null);

  const handleSnapToUrl = useCallback((url: string) => {
    setSnapToUrl(url);
  }, []);
  const status = result?.ingestion_status ?? null;
  const browserUseWorkflowId =
    result?.runtime_handle?.type === 'browser_use' ? result.runtime_handle.workflow_id : null;

  const isActive = isActiveStatus(status);
  const isTerminal = isTerminalStatus(status);

  // Derive what to show
  const showSpinner = isActive && !browserUseWorkflowId;
  const showBrowserViewer = isActive && !!browserUseWorkflowId;
  const showAssist = !isActive; // terminal or no ingestion
  const showMiniPlayer = isTerminal && !!browserUseWorkflowId;
  const showError = status === 'failed';

  // Header label
  const headerLabel = isActive ? 'CMS Ingestion' : 'CMS Assist';

  return (
    <aside className="flex h-full flex-shrink-0 flex-col overflow-hidden border-l border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-zinc-100 px-4 py-3 dark:border-zinc-800">
        <h2 className="text-xs font-semibold text-zinc-800 dark:text-zinc-200">
          {headerLabel}
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="h-6 w-6 p-0 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Body */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {/* Phase 1: Spinner */}
        {showSpinner && (
          <IngestionSpinner
            label={status === 'pending' ? 'Starting ingestion…' : 'Preparing browser agent…'}
          />
        )}

        {/* Phase 2: Live browser viewer */}
        {showBrowserViewer && (
          <div className="flex-1 overflow-hidden">
            <BrowserAgentViewer workflowId={browserUseWorkflowId} compact snapToUrl={snapToUrl} />
          </div>
        )}

        {/* Phase 3/4/5: Terminal or no ingestion — show assist content */}
        {showAssist && (
          <>
            {/* Error banner at top */}
            {showError && (
              <IngestionErrorBanner error={result?.ingestion_error ?? null} />
            )}

            {/* Assist panel */}
            {assistState.hasImport && (
              <div className="flex-1 overflow-y-auto px-4 py-3">
                <CaseIngestionAssistPanel
                  assistState={assistState}
                  currentStepId={currentStepId}
                  onSnapToUrl={browserUseWorkflowId ? handleSnapToUrl : undefined}
                />
              </div>
            )}

            {/* Mini-player at bottom */}
            {showMiniPlayer && (
              <div className="mt-auto">
                <BrowserAgentMiniPlayer workflowId={browserUseWorkflowId} snapToUrl={snapToUrl} />
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  );
};
